import { Grid } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { TaskExecutionAPI } from "../../../api";
import { useAlert } from "../../../lib/alert";
import NotFound from "../../app/NotFound";
import { Task, TaskExecution, TaskForm, TaskFormExecution } from "../../task";
import LinearFormExecutionComponent from "../../task/components/task-types/form/LinearFormExecutionComponent";

type LongTaskFormRoute = {
  taskExecutionId: string;
};

const ProcessExecutionLongTaskFormSharePage: FC<unknown> = () => {
  const { error } = useAlert();

  // fetch data from route
  const { taskExecutionId } = useParams<LongTaskFormRoute>();

  // state to control the task object according to the route, child component depends
  const [task, setTask] = useState<TaskForm>();

  // state to control the task execution object per route, child component depends
  const [execution, setExecution] = useState<TaskFormExecution>();

  // state to control whether current data is fully loaded
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const fetchTask = useCallback(
    async (taskExecutionId: string): Promise<Task> => {
      return TaskExecutionAPI.getSharedTask(taskExecutionId).then(
        (task: Task) => {
          setTask(task as TaskForm);
          return task;
        },
      );
    },
    [],
  );

  const fetchTaskExecution = useCallback(async (taskExecutionId: string) => {
    return TaskExecutionAPI.getShared(taskExecutionId).then(
      (execution: TaskExecution) => {
        setExecution(execution as TaskFormExecution);
        return execution;
      },
    );
  }, []);

  useEffect(() => {
    if (!taskExecutionId) return;
    const fetchData = async () => {
      const [task, execution] = await Promise.all([
        fetchTask(taskExecutionId),
        fetchTaskExecution(taskExecutionId),
      ]);
      setTask(task as TaskForm);
      setExecution(execution as TaskFormExecution);
    };

    try {
      setIsLoaded(false);
      fetchData();
    } catch {
      error("Failed to load task execution");
    } finally {
      setIsLoaded(true);
    }
  }, [error, fetchTask, fetchTaskExecution, taskExecutionId]);

  if (!taskExecutionId) {
    return <NotFound />;
  }

  return (
    <>
      {isLoaded && (
        <Grid
          container
          sx={{
            width: "100%",
            justifyContent: "center",
            my: 2,
          }}
        >
          <LinearFormExecutionComponent
            task={task as TaskForm}
            taskExecution={execution as TaskFormExecution}
          />
        </Grid>
      )}
    </>
  );
};

export default ProcessExecutionLongTaskFormSharePage;
